import React, { useEffect, useRef, useState } from 'react';
import { Box, Slider, Typography, Button } from '@mui/material';
import { RestartAlt } from '@mui/icons-material';

const PRESETS = {
  banner: {
    frameW: 360,
    frameH: 120,
    helperText: 'Drag to move and use the slider to zoom. Final image is 600 × 200 px.',
  },
  collage: {
    frameW: 420,
    frameH: 140,
    helperText: 'Drag to move and use the slider to zoom. Final image is 1200 × 400 px (3:1).',
  },
  square: {
    frameW: 280,
    frameH: 280,
    helperText: 'Drag to move and use the slider to zoom. Final image is 600 × 600 px (1:1).',
  },
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Crop with zoom + drag.
 * aspectMode: 'banner' (3:1 / 600×200), 'collage' (3:1 / 1200×400), or 'square' (1:1)
 * onChange receives { x, y, width, height } in the source image's natural pixels.
 */
const SquareImageCropper = ({
  src,
  initialCrop,
  onChange,
  aspectMode = 'banner',
  helperText,
}) => {
  const preset = PRESETS[aspectMode] || PRESETS.banner;
  const FRAME_W = preset.frameW;
  const FRAME_H = preset.frameH;
  const ASPECT = FRAME_W / FRAME_H;
  const imgRef = useRef(null);
  const dragRef = useRef(null);
  const [natural, setNatural] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [ready, setReady] = useState(false);

  const emit = (nextZoom, nextOffset, dims) => {
    const size = dims || natural;
    if (!size || !onChange) return;
    const base = Math.max(FRAME_W / size.w, FRAME_H / size.h);
    const scale = base * nextZoom;
    const displayedW = size.w * scale;
    const displayedH = size.h * scale;
    const imgLeft = (FRAME_W - displayedW) / 2 + nextOffset.x;
    const imgTop = (FRAME_H - displayedH) / 2 + nextOffset.y;
    onChange({
      x: Math.max(0, -imgLeft / scale),
      y: Math.max(0, -imgTop / scale),
      width: FRAME_W / scale,
      height: FRAME_H / scale,
    });
  };

  const clampOffset = (nextZoom, nextOffset, dims) => {
    const size = dims || natural;
    if (!size) return nextOffset;
    const base = Math.max(FRAME_W / size.w, FRAME_H / size.h);
    const scale = base * nextZoom;
    const displayedW = size.w * scale;
    const displayedH = size.h * scale;
    const maxX = Math.max(0, (displayedW - FRAME_W) / 2);
    const maxY = Math.max(0, (displayedH - FRAME_H) / 2);
    return {
      x: clamp(nextOffset.x, -maxX, maxX),
      y: clamp(nextOffset.y, -maxY, maxY),
    };
  };

  useEffect(() => {
    setReady(false);
    setNatural(null);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
  }, [src, aspectMode]);

  const handleLoad = (e) => {
    const w = e.currentTarget.naturalWidth;
    const h = e.currentTarget.naturalHeight;
    const dims = { w, h };
    setNatural(dims);

    let nextZoom = 1;
    let nextOffset = { x: 0, y: 0 };

    const cropW = Number(initialCrop?.width) || (Number(initialCrop?.size) > 0 ? Number(initialCrop.size) : 0);
    const cropH = Number(initialCrop?.height)
      || (Number(initialCrop?.size) > 0
        ? (aspectMode === 'square' ? Number(initialCrop.size) : Number(initialCrop.size) / ASPECT)
        : 0); // banner/collage share 3:1

    if (cropW > 0 && cropH > 0) {
      const base = Math.max(FRAME_W / w, FRAME_H / h);
      const scale = FRAME_W / cropW;
      nextZoom = clamp(scale / base, 1, 3);
      const appliedScale = base * nextZoom;
      const displayedW = w * appliedScale;
      const displayedH = h * appliedScale;
      nextOffset = {
        x: -Number(initialCrop.x || 0) * appliedScale - (FRAME_W - displayedW) / 2,
        y: -Number(initialCrop.y || 0) * appliedScale - (FRAME_H - displayedH) / 2,
      };
    }

    nextOffset = clampOffset(nextZoom, nextOffset, dims);
    setZoom(nextZoom);
    setOffset(nextOffset);
    setReady(true);
    emit(nextZoom, nextOffset, dims);
  };

  const applyZoom = (value) => {
    const nextZoom = clamp(value, 1, 3);
    const nextOffset = clampOffset(nextZoom, offset);
    setZoom(nextZoom);
    setOffset(nextOffset);
    emit(nextZoom, nextOffset);
  };

  const onPointerDown = (e) => {
    if (!natural) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = {
      x: e.clientX,
      y: e.clientY,
      ox: offset.x,
      oy: offset.y,
    };
  };

  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    const nextOffset = clampOffset(zoom, {
      x: dragRef.current.ox + (e.clientX - dragRef.current.x),
      y: dragRef.current.oy + (e.clientY - dragRef.current.y),
    });
    setOffset(nextOffset);
    emit(zoom, nextOffset);
  };

  const onPointerUp = () => {
    dragRef.current = null;
  };

  const reset = () => {
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    emit(1, { x: 0, y: 0 });
  };

  const base = natural ? Math.max(FRAME_W / natural.w, FRAME_H / natural.h) : 1;
  const scale = base * zoom;

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        sx={{
          width: FRAME_W,
          height: FRAME_H,
          mx: 'auto',
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 2,
          bgcolor: '#111',
          border: '1px solid #444',
          cursor: natural ? 'grab' : 'default',
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        <Box
          component="img"
          ref={imgRef}
          src={src}
          alt="Prize crop"
          draggable={false}
          onLoad={handleLoad}
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            width: natural ? `${natural.w}px` : '100%',
            height: natural ? `${natural.h}px` : '100%',
            maxWidth: 'none',
            transform: natural
              ? `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px)) scale(${scale})`
              : 'none',
            transformOrigin: 'center center',
            opacity: ready ? 1 : 0,
            pointerEvents: 'none',
          }}
        />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1.5, px: 1 }}>
        <Typography variant="caption" sx={{ color: '#999', minWidth: 36 }}>Zoom</Typography>
        <Slider
          size="small"
          min={1}
          max={3}
          step={0.01}
          value={zoom}
          onChange={(_, value) => applyZoom(value)}
          sx={{ color: '#FFD54F' }}
        />
        <Button
          size="small"
          startIcon={<RestartAlt />}
          onClick={reset}
          sx={{ color: '#bbb', textTransform: 'none', flexShrink: 0 }}
        >
          Reset
        </Button>
      </Box>
      <Typography variant="caption" sx={{ color: '#666', display: 'block', mt: 0.5, textAlign: 'center' }}>
        {helperText || preset.helperText}
      </Typography>
    </Box>
  );
};

export default SquareImageCropper;
